import { c as _c } from "react/compiler-runtime";
import Tooltip from '@precis-digital/kurama/Tooltip';
import { type ReactElement, type ReactNode } from 'react';
import type { ChartDataRow } from '../../../types';
import { defaultTFunction } from '../../../utils/generics';
import { ROW_SELECTION_COLUMN_ID } from '../constants';
import { useHovered } from '../hooks';
import { StyledCellContainer } from '../styled';
import type { TableHeadCellContentProps } from '../types';
import { getHeaderLabelData, getIsFilteredForColumnId, getSortDirectionForColumnId } from '../utils';
import { wrapHeaderWithDNDActivator } from '../utils/dnd';
import TableHeadCellFiltered from './TableHeadCellFiltered';
import TableHeadCellMoreMenu from './TableHeadCellMoreMenu';
import TableHeadCellRowSelection from './TableHeadCellRowSelection';
import TableHeadCellSortButton from './TableHeadCellSortButton';
const TableHeadCellContent = props => {
  const $ = _c(46);
  const {
    hovered,
    onMouseEnter,
    onMouseLeave
  } = useHovered();
  let canSort;
  let columnId;
  let context;
  let defaultSortDirection;
  let fixedSort;
  let headerContent;
  let justifyContent;
  let setSort;
  let sorted;
  let t;
  let t0;
  let t1;
  let textAlign;
  if ($[0] !== props) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const {
        columnDnDDragHandle: columnReorderDragHandle,
        ...t2
      } = props;
      context = t2;
      const {
        table,
        column: t3
      } = context;
      const {
        id: t4,
        getCanSort,
        columnDef: t5
      } = t3;
      columnId = t4;
      const {
        meta: columnMeta
      } = t5;
      const {
        sorting,
        columnFilters,
        setSort: t6,
        t: t7
      } = props.meta.chartCard;
      setSort = t6;
      t = t7 === undefined ? defaultTFunction : t7;
      if (columnId === ROW_SELECTION_COLUMN_ID) {
        t1 = <TableHeadCellRowSelection table={table} some={table.getIsSomeRowsSelected()} all={table.getIsAllRowsSelected()} t={t} />;
        break bb0;
      }
      const {
        element: labelElement,
        title: labelElementTitle,
        textAlign: t8,
        justifyContent: t9,
        defaultSortDirection: t10
      } = getHeaderLabelData(context);
      textAlign = t8;
      justifyContent = t9;
      defaultSortDirection = t10;
      headerContent = wrapHeaderWithDNDActivator(columnReorderDragHandle, labelElement);
      if (labelElementTitle != null) {
        headerContent = <Tooltip key="header-label-tooltip" kind="singleline" title={labelElementTitle}>{headerContent}</Tooltip>;
      }
      ({
        fixedSort
      } = columnMeta?.chartCard ?? {});
      canSort = getCanSort();
      sorted = getSortDirectionForColumnId(sorting, columnId);
      t0 = getIsFilteredForColumnId(columnFilters, columnId);
    }
    $[0] = props;
    $[1] = canSort;
    $[2] = columnId;
    $[3] = context;
    $[4] = defaultSortDirection;
    $[5] = fixedSort;
    $[6] = headerContent;
    $[7] = justifyContent;
    $[8] = setSort;
    $[9] = sorted;
    $[10] = t;
    $[11] = t0;
    $[12] = t1;
    $[13] = textAlign;
  } else {
    canSort = $[1];
    columnId = $[2];
    context = $[3];
    defaultSortDirection = $[4];
    fixedSort = $[5];
    headerContent = $[6];
    justifyContent = $[7];
    setSort = $[8];
    sorted = $[9];
    t = $[10];
    t0 = $[11];
    t1 = $[12];
    textAlign = $[13];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  const filtered = t0;
  let t2;
  if ($[14] !== canSort || $[15] !== columnId || $[16] !== defaultSortDirection || $[17] !== fixedSort || $[18] !== hovered || $[19] !== setSort || $[20] !== sorted || $[21] !== t) {
    t2 = !canSort || !hovered && sorted === false ? null : <TableHeadCellSortButton key="sort-button" sorted={sorted} defaultSortDirection={defaultSortDirection} fixedSort={fixedSort ?? false} setSort={setSort} columnId={columnId} t={t} />;
    $[14] = canSort;
    $[15] = columnId;
    $[16] = defaultSortDirection;
    $[17] = fixedSort;
    $[18] = hovered;
    $[19] = setSort;
    $[20] = sorted;
    $[21] = t;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  const sortButton = t2;
  let t3;
  if ($[23] !== filtered || $[24] !== t) {
    t3 = filtered ? <TableHeadCellFiltered key="filtered-icon" t={t} /> : null;
    $[23] = filtered;
    $[24] = t;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  const filteredIcon = t3;
  let t4;
  if ($[26] !== filteredIcon || $[27] !== headerContent || $[28] !== sortButton || $[29] !== textAlign) {
    t4 = textAlign === "left" ? [headerContent, sortButton, filteredIcon] : [filteredIcon, sortButton, headerContent];
    $[26] = filteredIcon;
    $[27] = headerContent;
    $[28] = sortButton;
    $[29] = textAlign;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  const labelAndSort = t4;
  let t5;
  if ($[31] !== context || $[32] !== hovered || $[33] !== onMouseLeave) {
    t5 = <TableHeadCellMoreMenu key="more-menu" visible={hovered} onClose={onMouseLeave} {...context} />;
    $[31] = context;
    $[32] = hovered;
    $[33] = onMouseLeave;
    $[34] = t5;
  } else {
    t5 = $[34];
  }
  let t6;
  if ($[35] !== justifyContent || $[36] !== labelAndSort) {
    t6 = <StyledCellContainer key="label-sort-container" justifyContent={justifyContent}>{labelAndSort}</StyledCellContainer>;
    $[35] = justifyContent;
    $[36] = labelAndSort;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let labelSortAndMoreMenu;
  if ($[38] !== t5 || $[39] !== t6 || $[40] !== textAlign) {
    labelSortAndMoreMenu = [t5, t6];
    if (textAlign === "left") {
      labelSortAndMoreMenu.reverse();
    }
    $[38] = t5;
    $[39] = t6;
    $[40] = textAlign;
    $[41] = labelSortAndMoreMenu;
  } else {
    labelSortAndMoreMenu = $[41];
  }
  let t7;
  if ($[42] !== labelSortAndMoreMenu || $[43] !== onMouseEnter || $[44] !== onMouseLeave) {
    t7 = <StyledCellContainer onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>{labelSortAndMoreMenu}</StyledCellContainer>;
    $[42] = labelSortAndMoreMenu;
    $[43] = onMouseEnter;
    $[44] = onMouseLeave;
    $[45] = t7;
  } else {
    t7 = $[45];
  }
  return t7;
};
export default TableHeadCellContent;